import { useCallback, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import polygonClipping, { Ring as PCRing } from 'polygon-clipping';
import proj4 from 'proj4';
import { exportIfc, IfcEntity, IfcWallEntity, IfcSpaceEntity, IfcOpeningVoid, IfcStorey, IfcPoint, IfcSiteGeoRef, IfcSpaceBoundary } from '@pointorama/ifc-exporter';
import { isAnnotationContextGroup } from '../../contexts/AnnotationContext';
import { useAnnotationTree } from './useAnnotationContext';
import { RendererContext } from '../../contexts/RendererContext';
import { Annotation } from '../../types/graphqlTypes';
import { useFlatAnnotationList } from './useFlatAnnotationList';
import { useResolveFloorGroup } from './useResolveFloorGroup';
import { useFloorProperties } from '../../modules/Properties/integration/annotations/FloorProperties';
import {
  getAreaPointsProperty,
  getRoomPolygonProperty,
  getRoomBaseProperty,
  getRoomCeilingProperty,
  getOutlineOuterRingProperty,
  getOutlineCavitiesProperty,
  getOpeningRoomIdsProperty,
  getOpeningKindProperty,
  getAnnotationNameProperty,
  getOpeningSketchWallEdgeIdProperty,
  getOpeningSketchOffsetProperty,
  getOpeningSketchLengthProperty,
  getOpeningSketchLowerBoundProperty,
  getOpeningSketchHeightProperty,
  getOpeningSketchKindProperty,
  getWallSketchNodesProperty,
  getWallSketchEdgesProperty,
  resolveLevelBounds,
  FALLBACK_INTERIOR_HEIGHT_M,
} from '@pointorama/potree-shared';
import { useFloorGroupsData } from './useFloorGroupsData';
import { OpeningKind } from 'shared-types';
import { computeOpeningRectangle } from './openingDxfGeometry';
import { extractOutlineRings, flattenRingComponents, hasClosedOutline, resolveOutlineRingsFromAnnotations, type OutlineRingComponent } from '../../potree/utils/skeleton/outlineRings';
import { useProject } from './useProject';
import { ProjectionSystems } from '../../utils/constants';
import type { PointoramaWallSketch } from '../../potree/utils/PointoramaWallSketch';
import { edgeIsCurved, subSpan, tessellateBand } from '../../potree/utils/skeleton/arcGeometry';

// Shared with the skeleton default wall top and level-bounds fallback.
const DEFAULT_WALL_HEIGHT = FALLBACK_INTERIOR_HEIGHT_M;
const DEFAULT_WALL_THICKNESS = 0.05;

export enum IfcExportTypes {
  'AREA',
  'ROOM',
  'OUTLINE',
}

/**
 * Convert polygon points to wall entities (one wall per edge).
 */
function polygonEdgesToWalls(points: Array<{ x: number; y: number; z: number }>, height: number, layerName?: string, namePrefix?: string): IfcWallEntity[] {
  if (points.length < 2) return [];

  const walls: IfcWallEntity[] = [];
  for (let i = 0; i < points.length; i++) {
    const start = points[i];
    const end = points[(i + 1) % points.length];

    walls.push({
      type: 'WALL',
      startPoint: { x: start.x, y: start.y, z: start.z },
      endPoint: { x: end.x, y: end.y, z: end.z },
      height,
      layerName,
      name: namePrefix ? `${namePrefix}_${i}` : undefined,
      thickness: DEFAULT_WALL_THICKNESS,
    });
  }

  return walls;
}

type Pt2 = { x: number; y: number };

/** Convert a polygon ring to a self-closing polygon-clipping ring. */
function ringToPC(ring: Pt2[]): PCRing {
  const out: PCRing = ring.map((p) => [p.x, p.y] as [number, number]);
  if (out.length > 0) out.push([out[0][0], out[0][1]]);
  return out;
}

/** Convert a polygon-clipping ring back to 2D points (drops the closing vertex). */
function pcToRing(ring: PCRing): Pt2[] {
  const out: Pt2[] = [];
  for (let i = 0; i < ring.length - 1; i++) out.push({ x: ring[i][0], y: ring[i][1] });
  return out;
}

/** Outline footprint minus the room interiors → the solid wall region (one component per polygon; wings split). */
function buildWallSolids(outerRing: Pt2[], cavities: Pt2[][], rooms: Pt2[][]): Array<{ outerLoop: Pt2[]; innerLoops: Pt2[][] }> {
  if (outerRing.length < 3) return [];

  const footprint: PCRing[] = [ringToPC(outerRing), ...cavities.map(ringToPC)];
  const roomPolys: PCRing[][] = rooms.filter((r) => r.length >= 3).map((r) => [ringToPC(r)]);

  const result = roomPolys.length > 0 ? polygonClipping.difference(footprint, ...roomPolys) : [footprint];

  const components: Array<{ outerLoop: Pt2[]; innerLoops: Pt2[][] }> = [];
  for (const poly of result) {
    if (poly.length === 0) continue;
    const outerLoop = pcToRing(poly[0]);
    if (outerLoop.length < 3) continue;
    const innerLoops = poly
      .slice(1)
      .map(pcToRing)
      .filter((r) => r.length >= 3);
    components.push({ outerLoop, innerLoops });
  }
  return components;
}

/** Ray-casting point-in-polygon test (XY). */
function pointInPolygon(pt: Pt2, ring: Pt2[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i].x;
    const yi = ring[i].y;
    const xj = ring[j].x;
    const yj = ring[j].y;
    if (yi > pt.y !== yj > pt.y && pt.x < ((xj - xi) * (pt.y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** Average of a ring's vertices. */
function polygonCentroid(ring: Pt2[]): Pt2 {
  let sx = 0;
  let sy = 0;
  for (const p of ring) {
    sx += p.x;
    sy += p.y;
  }
  return { x: sx / ring.length, y: sy / ring.length };
}

/**
 * An opening as a void: footprint across the wall (DXF rectangle solver),
 * sill/height from its corners, tagged DOOR/WINDOW. Null if it can't be fit or
 * is flat.
 */
function openingToVoid(openingId: string, outlineRings: { x: number; y: number; z: number }[][]): IfcOpeningVoid | null {
  const rect = computeOpeningRectangle(openingId, outlineRings);
  if (rect.fallback) {
    console.warn(`[IFC] Opening ${openingId} not cut: ${rect.reason}`);
    return null;
  }

  const pts = getAreaPointsProperty(openingId).value;
  if (!pts || pts.length < 2) return null;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const p of pts) {
    if (p.position.z < minZ) minZ = p.position.z;
    if (p.position.z > maxZ) maxZ = p.position.z;
  }
  const height = maxZ - minZ;
  if (!(height > 0)) return null;

  const name = getAnnotationNameProperty(openingId).value || 'opening';
  const kind = getOpeningKindProperty(openingId).value === OpeningKind.WINDOW ? 'WINDOW' : 'DOOR';
  return {
    footprint: rect.corners.map((c) => ({ x: c.x, y: c.y })),
    elevation: minZ,
    height,
    name,
    kind,
  };
}

/** Opening voids for the cut rooms (deduped — an interior door is cut once). */
function collectOpeningVoids(cutRoomIds: string[], openingIdsByRoomId: Record<string, string[]>, outlineRings: { x: number; y: number; z: number }[][]): IfcOpeningVoid[] {
  const seen = new Set<string>();
  const voids: IfcOpeningVoid[] = [];
  for (const roomId of cutRoomIds) {
    for (const openingId of openingIdsByRoomId[roomId] ?? []) {
      if (seen.has(openingId)) continue;
      seen.add(openingId);
      const v = openingToVoid(openingId, outlineRings);
      if (v) voids.push(v);
    }
  }
  return voids;
}

/** Tag entities with the storey (floor) they belong to, so the IFC export can group them per IfcBuildingStorey. */
function withStorey(entities: IfcEntity[], storeyId?: string | null): IfcEntity[] {
  if (!storeyId) return entities;
  return entities.map((e) => ({ ...e, storeyId }));
}

/**
 * Stable local origin for the export: the point cloud's world bounding-box min.
 * Subtracting it keeps every coordinate small and positive; the offset itself is
 * recorded on the IfcSite (ePset_MapConversion) so the georeference is recoverable.
 *
 * The CRS is taken from the project setting (authoritative), falling back to the
 * point cloud's own projection string. When a CRS is known we also derive the
 * WGS84 RefLatitude/RefLongitude and the EPSG name (ePset_ProjectedCRS).
 */
function getExportGeoref(
  pointcloud: { getBoundingBoxWorld: () => { min: { x: number; y: number; z: number } }; projection?: string } | undefined,
  crs?: { parameters: string; epsgCode: number },
): { origin?: IfcPoint; site?: IfcSiteGeoRef } {
  if (!pointcloud) return {};
  const min = pointcloud.getBoundingBoxWorld().min;
  const origin: IfcPoint = { x: min.x, y: min.y, z: min.z };

  // Prefer the project's configured CRS; fall back to the cloud's projection.
  const projection = crs?.parameters ?? pointcloud.projection;
  const crsName = crs ? `EPSG:${crs.epsgCode}` : undefined;
  if (!projection) {
    console.warn('[IFC] No CRS for this project; exporting offset (ePset_MapConversion) without lat/long.');
    return { origin, site: { refElevation: origin.z } };
  }

  try {
    const transform = projection.includes('EPSG') ? proj4(projection, 'WGS84') : (proj4.defs('pointcloud', projection), proj4('pointcloud', 'WGS84'));
    const [refLongitude, refLatitude] = transform.forward([origin.x, origin.y]);
    return { origin, site: { refLatitude, refLongitude, refElevation: origin.z, crsName } };
  } catch (e) {
    console.warn('[IFC] Could not derive lat/long from CRS; exporting offset (ePset_MapConversion) only.', e);
    return { origin, site: { refElevation: origin.z, crsName } };
  }
}

function downloadIfc(content: string, filename = 'export.ifc') {
  const blob = new Blob([content], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

// Group (floor/surface) display names by identifier — storey/layer names.
const useGroupNameByIdentifier = (): Record<string, string> => {
  const annotationTree = useAnnotationTree();
  return useMemo(() => {
    const map: Record<string, string> = {};
    for (const item of annotationTree) {
      if (isAnnotationContextGroup(item)) {
        map[item.identifier] = item.name;
      }
    }
    return map;
  }, [annotationTree]);
};

// Project CRS (proj4 params + EPSG) for georeferencing the export, if configured.
const useExportCrs = () => {
  const { data: projectData } = useProject();
  return useMemo(() => {
    const ps = projectData?.projectById?.settings?.projectionSystem;
    return ps && ps in ProjectionSystems ? ProjectionSystems[ps as keyof typeof ProjectionSystems] : undefined;
  }, [projectData]);
};

// Storeys, georef, serialize, download. Client-side only: IFC exports are
// not tracked server-side.
const useDownloadIfc = () => {
  const { viewer } = useContext(RendererContext);
  const { projectId = '' } = useParams();
  const { getFloorPlanePosition } = useFloorProperties(projectId);
  const groupNameByIdentifier = useGroupNameByIdentifier();
  const exportCrs = useExportCrs();

  return useCallback(
    (entities: IfcEntity[], opts?: { spaceBoundaries?: IfcSpaceBoundary[]; floorHeightFallback?: Map<string, number> }) => {
      if (entities.length === 0) return;

      // One IfcBuildingStorey per floor present in the export. Elevation from
      // the floor plane position, falling back to the sketch's own floor height.
      const storeyIds = new Set<string>();
      for (const e of entities) if (e.storeyId) storeyIds.add(e.storeyId);
      const storeys: IfcStorey[] = [...storeyIds].map((id) => ({
        id,
        name: groupNameByIdentifier[id] ?? 'Storey',
        elevation: getFloorPlanePosition(id)?.z ?? opts?.floorHeightFallback?.get(id) ?? 0,
      }));

      // Offset geometry by the point cloud's world origin so georeferenced projects
      // keep small, positive coordinates (the origin rides on the IfcSite placement).
      const { origin, site } = getExportGeoref(viewer?.scene?.pointclouds?.[0], exportCrs);
      downloadIfc(exportIfc(entities, { storeys, origin, site, spaceBoundaries: opts?.spaceBoundaries }), `export_${Date.now()}.ifc`);
    },
    [viewer, getFloorPlanePosition, groupNameByIdentifier, exportCrs],
  );
};

export const useExportIFC = () => {
  const { allAnnotations } = useFlatAnnotationList();
  // Sketches filed in a room folder carry the folder id — resolve to the floor
  // so storeys/elevations group correctly.
  const resolveFloorGroup = useResolveFloorGroup();
  const { projectId = '' } = useParams();
  const downloadMergedIfc = useDownloadIfc();

  const { getFloorPlanePosition } = useFloorProperties(projectId);
  const groupNameByIdentifier = useGroupNameByIdentifier();
  const { loadLevelPlanes } = useFloorGroupsData({ id: projectId });

  const getWallHeight = useCallback(
    (groupIdentifier?: string | null): number => {
      if (!groupIdentifier) return DEFAULT_WALL_HEIGHT;
      // Level bounds filter unset {0,0,0} ceilings (which previously exported
      // |floor elevation| as a wall height) and fall back to the next floor on
      // legacy groups so multi-storey exports keep their current heights.
      const bounds = resolveLevelBounds(loadLevelPlanes(), groupIdentifier);
      if (bounds && bounds.ceilingSource !== 'fallback') return bounds.interiorHeight;
      return DEFAULT_WALL_HEIGHT;
    },
    [loadLevelPlanes],
  );

  /** The floor's MERGED outline rings (`role:'OUTLINE'` cycles of its wall
   *  sketch), or empty — decides whether legacy OUTLINE annotations are
   *  superseded tombstones. */
  const mergedOutlineComponents = useCallback(
    (floorGroupId: string): OutlineRingComponent[] => {
      const sketch = allAnnotations.find((a) => a.type === 'WALL_SKETCH' && a.groupIdentifier === floorGroupId);
      if (!sketch) return [];
      try {
        return extractOutlineRings(getWallSketchNodesProperty(sketch.identifier).value, getWallSketchEdgesProperty(sketch.identifier).value);
      } catch {
        return [];
      }
    },
    [allAnnotations],
  );

  const annotationToIfcEntities = useCallback(
    (annotation: Annotation, groupName?: string): IfcEntity[] => {
      const name = getAnnotationNameProperty(annotation.identifier).value;
      switch (annotation.type) {
        case 'AREA': {
          const areaPoints = getAreaPointsProperty(annotation.identifier).value;
          const points = areaPoints.map((p) => p.position);
          return polygonEdgesToWalls(points, DEFAULT_WALL_HEIGHT, groupName || 'Areas', name);
        }
        case 'ROOM': {
          const polygon = getRoomPolygonProperty(annotation.identifier).value;
          const base = getRoomBaseProperty(annotation.identifier).value;
          const ceiling = getRoomCeilingProperty(annotation.identifier).value;
          const height = ceiling && base ? Math.abs(ceiling.position.z - base.position.z) || DEFAULT_WALL_HEIGHT : DEFAULT_WALL_HEIGHT;
          return polygonEdgesToWalls(polygon, height, groupName || 'Rooms', name);
        }
        case 'OUTLINE': {
          // Superseded tombstone: the floor's merged outline exports via its
          // wall sketch (thin walls / full walls) — skip the legacy copy.
          if (annotation.groupIdentifier && mergedOutlineComponents(annotation.groupIdentifier).length > 0) return [];
          // Selected on its own: thin perimeter walls along the outer ring and each cavity.
          const outerRing = getOutlineOuterRingProperty(annotation.identifier).value;
          const cavities = getOutlineCavitiesProperty(annotation.identifier).value ?? [];
          const height = getWallHeight(annotation.groupIdentifier);
          const layer = groupName || 'Outlines';
          const rings = [outerRing, ...cavities].filter((ring) => ring.length >= 2);
          return rings.flatMap((ring, i) => polygonEdgesToWalls(ring, height, layer, i === 0 ? name || 'outline' : `${name || 'outline'}_void_${i}`));
        }
        case 'WALL_SKETCH': {
          // The sketch's merged outline as thin perimeter walls (the sketch's
          // wall skin has its own export path); nothing when unmerged.
          const height = getWallHeight(annotation.groupIdentifier);
          const layer = groupName || 'Outlines';
          const rings = annotation.groupIdentifier ? flattenRingComponents(mergedOutlineComponents(annotation.groupIdentifier)) : [];
          return rings.filter((ring) => ring.length >= 2).flatMap((ring, i) => polygonEdgesToWalls(ring, height, layer, i === 0 ? name || 'outline' : `${name || 'outline'}_void_${i}`));
        }
        default:
          return [];
      }
    },
    [getWallHeight, mergedOutlineComponents],
  );

  // Built here (not in annotationToIfcEntities) because it needs the export-list
  // context — which rooms are cutouts and their openings. Wing-based: one call
  // per resolved outline ring component (merged sketch outline or legacy
  // OUTLINE annotation alike).
  const buildOutlineWall = useCallback(
    (wing: { name: string; component: OutlineRingComponent; floorGroupId: string }, cutRoomIds: string[], openingIdsByRoomId: Record<string, string[]>, snapRings: { x: number; y: number; z: number }[][], groupName?: string): IfcEntity[] => {
      const { outerRing, holes } = wing.component;
      if (outerRing.length < 3) return [];

      const floorHeight = outerRing[0]?.z ?? getFloorPlanePosition(wing.floorGroupId)?.z ?? 0;
      const wallHeight = getWallHeight(wing.floorGroupId);
      const layerName = groupName || 'Outlines';

      const roomPolygons = cutRoomIds.map((id) => getRoomPolygonProperty(id).value).filter((poly) => poly.length >= 3);
      const solids = buildWallSolids(outerRing, holes, roomPolygons);

      // Assign each opening void to the wall component (wing) that contains it.
      const voids = collectOpeningVoids(cutRoomIds, openingIdsByRoomId, snapRings);
      const voidsByComponent: IfcOpeningVoid[][] = solids.map(() => []);
      for (const v of voids) {
        const c = polygonCentroid(v.footprint);
        let idx = solids.findIndex((s) => pointInPolygon(c, s.outerLoop));
        if (idx < 0) idx = 0;
        voidsByComponent[idx].push(v);
      }

      return solids.map((comp, i) => ({
        type: 'WALL_OUTLINE' as const,
        outerLoop: comp.outerLoop,
        innerLoops: comp.innerLoops,
        elevation: floorHeight,
        height: wallHeight,
        layerName,
        name: solids.length > 1 ? `${wing.name}_${i}` : wing.name,
        openings: voidsByComponent[i],
      }));
    },
    [getWallHeight, getFloorPlanePosition],
  );

  const isExportIFCPossible = useCallback(
    (annotations?: Annotation[]): boolean => {
      if (!projectId) return false;
      const list = annotations ?? allAnnotations;
      return list.some((annotation) => annotation?.type in IfcExportTypes);
    },
    [projectId, allAnnotations],
  );

  // Build only; the floors export merges this with the layout entities.
  const buildObjectsIFC = useCallback(
    (annotations?: Annotation[], opts?: { fullWallsFloors?: Set<string> }): IfcEntity[] => {
      if (!isExportIFCPossible(annotations) && !opts?.fullWallsFloors?.size) return [];

      const list = annotations ?? allAnnotations;

      // Reverse index over ALL annotations: room id -> linked opening ids.
      const openingIdsByRoomId: Record<string, string[]> = {};
      for (const a of allAnnotations) {
        if (a.type !== 'OPENING') continue;
        for (const roomId of getOpeningRoomIdsProperty(a.identifier).value ?? []) {
          (openingIdsByRoomId[roomId] ??= []).push(a.identifier);
        }
      }

      // Which floors export as full walls. Without explicit intent (e.g. a whole
      // floor passed directly), any floor with an outline in the payload
      // qualifies — a legacy OUTLINE annotation or a wall sketch carrying a
      // merged (closed) outline.
      const fullWallsFloors =
        opts?.fullWallsFloors ??
        new Set([
          ...list.filter((a) => a.type === 'OUTLINE' && a.groupIdentifier).map((a) => a.groupIdentifier as string),
          ...list.filter((a) => a.type === 'WALL_SKETCH' && a.groupIdentifier && hasClosedOutline(allAnnotations, a.groupIdentifier)).map((a) => a.groupIdentifier as string),
        ]);

      const entities: IfcEntity[] = [];
      const handled = new Set<string>();

      // Full-walls floors: resolve the floor's outline rings (merged sketch
      // outline first, legacy OUTLINE annotations fallback) + rooms and build
      // solid walls, cutting each room out of the wing that contains it
      // (fallback: first).
      for (const floorId of fullWallsFloors) {
        const wings = resolveOutlineRingsFromAnnotations(allAnnotations, floorId);
        if (wings.length === 0) continue;
        const floorAnnotations = allAnnotations.filter((a) => a.groupIdentifier === floorId);
        const legacyOutlines = floorAnnotations.filter((a) => a.type === 'OUTLINE');
        const rooms = floorAnnotations.filter((a) => a.type === 'ROOM');
        const groupName = groupNameByIdentifier[floorId];
        const snapRings = flattenRingComponents(wings);
        // Single legacy outline keeps its (possibly user-renamed) name.
        const baseName = (legacyOutlines.length === 1 && (getAnnotationNameProperty(legacyOutlines[0].identifier).value || null)) || 'outline';

        const cutRoomIdsByWing: string[][] = wings.map(() => []);
        for (const room of rooms) {
          const poly = getRoomPolygonProperty(room.identifier).value;
          if (poly.length < 3) continue;
          const center = polygonCentroid(poly);
          let idx = wings.findIndex((w) => w.outerRing.length >= 3 && pointInPolygon(center, w.outerRing));
          if (idx < 0) idx = 0;
          cutRoomIdsByWing[idx].push(room.identifier);
        }

        wings.forEach((component, i) => {
          const name = wings.length > 1 ? `${baseName}_${i}` : baseName;
          entities.push(...withStorey(buildOutlineWall({ name, component, floorGroupId: floorId }, cutRoomIdsByWing[i], openingIdsByRoomId, snapRings, groupName), floorId));
        });
        // Legacy outlines are consumed by the full-walls build (as source or
        // tombstone); the sketch row must not additionally export thin walls.
        legacyOutlines.forEach((o) => handled.add(o.identifier));
        const sketch = floorAnnotations.find((a) => a.type === 'WALL_SKETCH');
        if (sketch) handled.add(sketch.identifier);
        rooms.forEach((room) => handled.add(room.identifier));
      }

      // Everything else in the payload exports individually. Openings are skipped:
      // they're only meaningful as voids cut into full-walls floors, and on their
      // own they render as not-very-useful thin walls.
      for (const annotation of list) {
        if (handled.has(annotation.identifier) || annotation.type === 'OPENING') continue;
        const floorGroupId = resolveFloorGroup(annotation.groupIdentifier);
        const groupName = floorGroupId ? groupNameByIdentifier[floorGroupId] : undefined;
        entities.push(...withStorey(annotationToIfcEntities(annotation, groupName), floorGroupId));
      }

      return entities;
    },
    [allAnnotations, groupNameByIdentifier, annotationToIfcEntities, buildOutlineWall, isExportIFCPossible, resolveFloorGroup],
  );

  const onExportIFC = useCallback(
    async (annotations?: Annotation[], opts?: { fullWallsFloors?: Set<string> }) => {
      downloadMergedIfc(buildObjectsIFC(annotations, opts));
    },
    [buildObjectsIFC, downloadMergedIfc],
  );

  return [onExportIFC, isExportIFCPossible, buildObjectsIFC] as const;
};

/** Exporter correlation id. Skeleton edge ids repeat across floors (rs_e_1, …), so qualify by floor group. */
const wallExportId = (floorId: string | null, edgeId: string) => `${floorId ?? ''}::${edgeId}`;

/**
 * Opening voids per skeleton edge, from the floor's parametric OPENING_SKETCHes.
 * Sill = lowerBound (absolute Z), head = sill + height, both falling back to
 * the edge's wall band.
 */
function buildOpeningVoidsByEdge(skeleton: PointoramaWallSketch, openingSketches: ReadonlyArray<{ identifier: string; groupIdentifier: string | null }>): Map<string, IfcOpeningVoid[]> {
  const byEdge = new Map<string, IfcOpeningVoid[]>();
  const nodeById = new Map(skeleton.getNodes().map((n) => [n.id, n]));

  for (const os of openingSketches) {
    // Skip other floors' openings — they could resolve against a same-named edge here.
    if (os.groupIdentifier && os.groupIdentifier !== skeleton.groupIdentifier) continue;

    let edgeId = '';
    let offset: number;
    let length: number;
    let lower: number | null | undefined;
    let heightVal: number | null | undefined;
    let kind: 'DOOR' | 'WINDOW';
    let name: string;
    try {
      edgeId = getOpeningSketchWallEdgeIdProperty(os.identifier).value || '';
      offset = getOpeningSketchOffsetProperty(os.identifier).value ?? 0;
      length = getOpeningSketchLengthProperty(os.identifier).value ?? 0;
      lower = getOpeningSketchLowerBoundProperty(os.identifier).value;
      heightVal = getOpeningSketchHeightProperty(os.identifier).value;
      kind = getOpeningSketchKindProperty(os.identifier).value === OpeningKind.WINDOW ? 'WINDOW' : 'DOOR';
      name = getAnnotationNameProperty(os.identifier).value || 'opening';
    } catch {
      continue; // property service not ready
    }
    if (!edgeId || length <= 1e-4) continue;
    const edge = skeleton.getEdge(edgeId);
    if (!edge) continue; // edge belongs to another wall sketch
    const a = nodeById.get(edge.startNodeId);
    const b = nodeById.get(edge.endNodeId);
    if (!a || !b) continue;

    const band = skeleton.getEdgeExtrusionZ(edgeId);
    const sill = lower ?? band.bottom;
    const head = heightVal != null ? sill + heightVal : band.top;
    if (!(head - sill > 0)) continue;

    const L = edge.leftSize || 0.1;
    const R = edge.rightSize || 0.1;
    const start = { x: a.position.x, y: a.position.y };
    const end = { x: b.position.x, y: b.position.y };

    let footprint: Pt2[];
    if (edgeIsCurved(edge)) {
      // Arc-band footprint so the void tracks the curved wall mass.
      const span = subSpan(start, end, edge.bulge, offset, offset + length);
      footprint = tessellateBand(span.start, span.end, span.bulge, L + 0.05, R + 0.05);
    } else {
      const dx = end.x - start.x;
      const dy = end.y - start.y;
      const len = Math.hypot(dx, dy);
      if (len < 1e-9) continue;
      const dir = { x: dx / len, y: dy / len };
      const perpLeft = { x: -dir.y, y: dir.x };
      const p0 = { x: start.x + dir.x * offset, y: start.y + dir.y * offset };
      const p1 = { x: start.x + dir.x * (offset + length), y: start.y + dir.y * (offset + length) };
      // Rectangle across the full asymmetric wall band (−R .. +L of the centerline).
      footprint = [
        { x: p0.x - perpLeft.x * R, y: p0.y - perpLeft.y * R },
        { x: p1.x - perpLeft.x * R, y: p1.y - perpLeft.y * R },
        { x: p1.x + perpLeft.x * L, y: p1.y + perpLeft.y * L },
        { x: p0.x + perpLeft.x * L, y: p0.y + perpLeft.y * L },
      ];
    }

    const list = byEdge.get(edgeId) ?? [];
    list.push({ footprint, elevation: sill, height: head - sill, name, kind });
    byEdge.set(edgeId, list);
  }
  return byEdge;
}

/**
 * One IfcWallStandardCase per skeleton edge: axis shifted by (L−R)/2 so the
 * centered wall (thickness L+R) occupies the edge's true asymmetric band.
 * Shifted axes can miss each other by mm at shared nodes — accepted: no joins
 * are declared, Revit's auto-join absorbs it (tested as the cleanest import).
 * Curved edges keep the chord as axis and export the arc band as body.
 */
function buildSkeletonFloorWalls(skeleton: PointoramaWallSketch, groupName: string | undefined, openingsByEdge: Map<string, IfcOpeningVoid[]>): IfcEntity[] {
  const nodeById = new Map(skeleton.getNodes().map((n) => [n.id, n]));
  const walls: IfcWallEntity[] = [];

  for (const edge of skeleton.getEdges()) {
    const a = nodeById.get(edge.startNodeId);
    const b = nodeById.get(edge.endNodeId);
    if (!a || !b) continue;

    const start = { x: a.position.x, y: a.position.y };
    const end = { x: b.position.x, y: b.position.y };
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-9) continue;

    const L = edge.leftSize || 0.1;
    const R = edge.rightSize || 0.1;
    const thickness = L + R;
    // Center the axis in the asymmetric band (left of direction = +perp).
    const shift = (L - R) / 2;
    const sx = (-dy / len) * shift;
    const sy = (dx / len) * shift;

    const { bottom, top } = skeleton.getEdgeExtrusionZ(edge.id);

    walls.push({
      type: 'WALL',
      id: wallExportId(skeleton.groupIdentifier, edge.id),
      startPoint: { x: start.x + sx, y: start.y + sy, z: bottom },
      endPoint: { x: end.x + sx, y: end.y + sy, z: bottom },
      height: top - bottom,
      thickness,
      layerName: groupName || 'Walls',
      name: edge.name || `wall_${edge.id.slice(0, 8)}`,
      openings: openingsByEdge.get(edge.id) ?? [],
      bodyFootprint: edgeIsCurved(edge) ? tessellateBand(start, end, edge.bulge, L, R) : undefined,
    });
  }
  return walls;
}

/** Every room id referenced by a skeleton's edges and nodes. */
function collectSkeletonRoomIds(skeleton: PointoramaWallSketch): Set<string> {
  const ids = new Set<string>();
  for (const e of skeleton.getEdges()) for (const rid of e.roomIds ?? []) ids.add(rid);
  for (const n of skeleton.getNodes()) for (const rid of n.roomIds ?? []) ids.add(rid);
  return ids;
}

/**
 * One IfcSpace per room referenced by this skeleton. Footprint comes from the
 * room polygon; vertical extent from the room's base/ceiling planes. Rooms
 * already emitted (shared across floors) are skipped via `seenRoomIds`.
 */
function buildSkeletonSpaces(skeleton: PointoramaWallSketch, seenRoomIds: Set<string>): IfcEntity[] {
  const spaces: IfcSpaceEntity[] = [];
  const roomIds = collectSkeletonRoomIds(skeleton);
  if (roomIds.size === 0) {
    console.warn(`[IFC] No rooms on skeleton "${skeleton.groupIdentifier ?? '?'}" — its edges/nodes carry no roomIds, so no IfcSpace is written.`);
  }
  for (const roomId of roomIds) {
    if (seenRoomIds.has(roomId)) continue;
    const poly = getRoomPolygonProperty(roomId).value;
    if (!poly || poly.length < 3) {
      console.warn(`[IFC] Room "${roomId}" skipped — no room_polygon (${poly?.length ?? 0} pts).`);
      continue;
    }

    const baseZ = getRoomBaseProperty(roomId).value.position.z;
    const ceilZ = getRoomCeilingProperty(roomId).value.position.z;
    const elevation = Math.min(baseZ, ceilZ);
    const height = Math.abs(ceilZ - baseZ);
    if (!(height > 0)) {
      console.warn(`[IFC] Room "${roomId}" skipped — non-positive height (base z=${baseZ}, ceiling z=${ceilZ}).`);
      continue;
    }

    seenRoomIds.add(roomId);
    spaces.push({
      type: 'SPACE',
      id: roomId,
      footprint: poly.map((p) => ({ x: p.x, y: p.y })),
      elevation,
      height,
      name: getAnnotationNameProperty(roomId).value || `room_${roomId.slice(0, 8)}`,
    });
  }
  return spaces;
}

/**
 * Room↔wall bonds for a skeleton: each edge contributes a boundary to every room
 * it borders (via `edge.roomIds`). The exporter drops any whose room or wall
 * produced no entity, so listing every pair here is safe.
 */
function buildSkeletonSpaceBoundaries(skeleton: PointoramaWallSketch): IfcSpaceBoundary[] {
  const boundaries: IfcSpaceBoundary[] = [];
  for (const edge of skeleton.getEdges()) {
    for (const rid of edge.roomIds ?? []) boundaries.push({ spaceId: rid, wallId: wallExportId(skeleton.groupIdentifier, edge.id) });
  }
  return boundaries;
}

// Layout persona export to IFC: whole floors. Per floor, every wall sketch
// exports one IfcWallStandardCase per edge (openings cut as voids), and the
// rooms it references become IfcSpaces with room↔wall space boundaries.
// Sketch objects are not exported (no IFC entity for them yet).
type LayoutIfcBuild = { entities: IfcEntity[]; spaceBoundaries: IfcSpaceBoundary[]; floorHeightFallback: Map<string, number> };

export const useExportLayoutIFC = () => {
  const { viewer } = useContext(RendererContext);
  const groupNameByIdentifier = useGroupNameByIdentifier();
  const downloadMergedIfc = useDownloadIfc();

  // Build only.
  const buildLayoutIFC = useCallback(
    (floorGroupIds: string[]): LayoutIfcBuild => {
      const entities: IfcEntity[] = [];
      const spaceBoundaries: IfcSpaceBoundary[] = [];
      const floorHeightFallback = new Map<string, number>();
      if (!viewer) return { entities, spaceBoundaries, floorHeightFallback };

      const seenRoomIds = new Set<string>();
      for (const floorId of floorGroupIds) {
        const groupName = groupNameByIdentifier[floorId];
        for (const skeleton of viewer.scene.wallSketches.filter((ws: PointoramaWallSketch) => ws.groupIdentifier === floorId)) {
          if (!floorHeightFallback.has(floorId)) floorHeightFallback.set(floorId, skeleton.getFloorHeight());
          const openingsByEdge = buildOpeningVoidsByEdge(skeleton, viewer.scene.openingSketches ?? []);
          entities.push(...withStorey(buildSkeletonFloorWalls(skeleton, groupName, openingsByEdge), floorId));
          entities.push(...withStorey(buildSkeletonSpaces(skeleton, seenRoomIds), floorId));
          spaceBoundaries.push(...buildSkeletonSpaceBoundaries(skeleton));
        }
      }

      return { entities, spaceBoundaries, floorHeightFallback };
    },
    [viewer, groupNameByIdentifier],
  );

  const onExportLayoutIFC = useCallback(
    (floorGroupIds: string[]) => {
      const { entities, spaceBoundaries, floorHeightFallback } = buildLayoutIFC(floorGroupIds);
      downloadMergedIfc(entities, { spaceBoundaries, floorHeightFallback });
    },
    [buildLayoutIFC, downloadMergedIfc],
  );

  return { onExportLayoutIFC, buildLayoutIFC };
};

// Objects (as-built floors) + layout (standardised floors) in one file.
export const useExportFloorsIFC = () => {
  const [, , buildObjectsIFC] = useExportIFC();
  const { buildLayoutIFC } = useExportLayoutIFC();
  const downloadMergedIfc = useDownloadIfc();

  const onExportFloorsIFC = useCallback(
    (input: { objects: Annotation[]; fullWallsFloors?: Set<string>; layoutFloorIds: string[] }) => {
      const layout = buildLayoutIFC(input.layoutFloorIds);
      const entities = [...buildObjectsIFC(input.objects, { fullWallsFloors: input.fullWallsFloors }), ...layout.entities];
      downloadMergedIfc(entities, { spaceBoundaries: layout.spaceBoundaries, floorHeightFallback: layout.floorHeightFallback });
    },
    [buildObjectsIFC, buildLayoutIFC, downloadMergedIfc],
  );

  return { onExportFloorsIFC };
};
